import { PreviewModalComponent } from './preview-modal/preview-modal.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MarketEditComponent } from '../../market-edit/market-edit.component';
import { ReportCreateComponent } from '../../report-create/report-create.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormGroup, Validators, FormBuilder } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Market } from '../../../api/models/market-model';
import { Organization } from '../../../api/models/organization';
import { ClientEditService } from './client-edit.service';
import { FileUploadService } from '../../../api/services/file-upload.service';
import { DeleteModalComponent } from '../../delete-modal/delete-modal.component';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/do';
import { UserService } from '../../../api/services/user.service';
import { ClientLogoService } from '../../services/client-logo.service';
import { MarketService } from '../../../api/services/market.service';
import { Address } from '../../../api/models/address';
import { AuthService } from '../../../api/services/auth.service';
import { Report } from '../../../api/models/report';
import { ThemeService } from '../../services/theme.service';
import { PreferencesService } from '../../../api/services/preferences.service';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { BillToAddress } from '../../../api/models/bill-to-address';
import { BillTo } from '../../../api/models/bill-to';
import { ProjectService } from '../../../api/services/project.services';
import { RoleRequest } from '../../../api/models/role-assignment-map';


@Component({
  selector: 'app-client-edit',
  templateUrl: './client-edit.component.html',
  styleUrls: ['./client-edit.component.scss']
})
export class ClientEditComponent implements OnInit {

  @Input() client: Organization;

  @Output() leaveEdit = new EventEmitter<Organization>();

  clientForm: FormGroup;

  editing = true;

  hasLogo = false;

  canDeleteMarket = false;

  canUpdateMarket = false;

  logo: any;

  theme: string;

  markets: Market[] = [];

  reports: Report[] = [];

  loading = false;

  loadingSearch = false;

  selectedSpecialField: string;

  specialFields: string[];

  broadcastMessageValue: string = '';

  constructor(
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private clientService: ClientEditService,
    private fileUploadService: FileUploadService,
    private marketService: MarketService,
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    private router: Router,
    private userService: UserService,
    private clientLogoService: ClientLogoService,
    private themeService: ThemeService,
    private preferencesService: PreferencesService,
    private authService: AuthService,
    private projectService: ProjectService,
  ) {
  }

  goBack() {
    this.leaveEdit.emit();
  }

  findLogo(logoId: string) {
    if (!logoId) { return; }
    this.fileUploadService.get(logoId)
      .subscribe(logo => {
        this.hasLogo = true;
        this.logo = logo;
      });
  }

  onLogoUploaded(id: string) {
    this.findLogo(id);
    this.clientService
      .saveLogo(this.client['Organization ID'], id)
      .subscribe(() => {
        this.onUpdateSuccess();
        this.clientLogoService.refreshClientLogo();
      });
  }

  onThemeSelected(themeName: string) {
    this.theme = themeName;
    this.clientService
      .saveTheme(this.client['Organization ID'], themeName)
      .subscribe(() => {
        this.onUpdateSuccess();
        this.themeService.refreshTheme();
      });
  }

  openThemeModal(content) {
    const modalRef = this.modalService.open(PreviewModalComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.theme = this.theme;
    modalRef.componentInstance.hasLogo = this.hasLogo;
    modalRef.componentInstance.logo = this.logo;
  }

  deleteLogo() {
    this.hasLogo = false;
    this.clientService.deleteLogo(this.client['Organization ID'])
      .subscribe(() => {
        this.onUpdateSuccess.bind(this);
        this.clientLogoService.refreshClientLogo();
      });
  }

  deleteClient() {
    const modalRef = this.modalService.open(DeleteModalComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.id = this.client['Organization ID'];
    modalRef.componentInstance.name = this.client['Name'];
    modalRef.componentInstance.fn = () => {
      this.userService.removeOrganization(this.client['Organization ID']).subscribe(() => {
        modalRef.close();
        this.leaveEdit.emit();
      });
    };
  }

  editMarket(market: Market) {
    forkJoin(this.marketService.getAddresses(market.id), this.marketService.getBillingAddresses(market.id))
      .subscribe(addresses => {
        this.openMarketModal('Edit', market, addresses[0], addresses[1]);
      });
  }

  deleteMarket(market: Market) {
    this.projectService.getActiveProjectCount(market.id).subscribe(count => {
      const modalRef = this.modalService.open(DeleteModalComponent, {
        backdrop: 'static',
        keyboard: false
      });

      modalRef.componentInstance.name = market.name;
      let additionalMsg = '';
      if(count > 0) {
        if(count === 1) {
          additionalMsg = `There is <strong>${count} active project</strong> associated with this market. `;
        } else {
          additionalMsg = `There are <strong>${count} active projects</strong> associated with this market. `; 
        }
      }  
      modalRef.componentInstance.bodyMsg = additionalMsg + `Are you sure you want to delete the market <strong>${market.name}</strong>?`;
      modalRef.componentInstance.fn = () => {
        const id = market.id;
        this.marketService.deleteMarket(id)
          .subscribe(() => {
            modalRef.close();
            this.markets = this.markets.filter(m => m.id !== market.id);
          });
      };
    });
  }
  
  createMarket() {
    this.openMarketModal('Create');
  }

  createReport() {
    this.openReportModal();
  }

  deleteReport(report: Report) {
    const modalRef = this.modalService.open(DeleteModalComponent, {
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.name = report.name;
    modalRef.componentInstance.fn = () => {
      this.userService.deleteReport(report.id).subscribe(() => {
        modalRef.close();
        this.getReports(report.organizationId);
      });
    };
  }

  openReportModal() {
    const modalRef = this.modalService.open(ReportCreateComponent, {
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.organizationId = this.client['Organization ID'];

    modalRef.result.then(() => {
      this.getReports(this.client['Organization ID']);
    }).catch(() => { });
  }

  openMarketModal(action, market: Market = new Market({ customerId: this.client['Organization ID'], customerName: this.client.Name }), addresses: Address[] = [], billTo: BillTo = new BillTo(), specialFieldValue: string = this.selectedSpecialField) {
    const modalRef = this.modalService.open(MarketEditComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });

    modalRef.result.then(() => {
      this.onUpdateSuccess();
      this.getMarketsV2(this.client['Organization ID']);
    }).catch(() => {
      this.getMarketsV2(this.client['Organization ID']);
    });

    modalRef.componentInstance.market = market;
    modalRef.componentInstance.addresses = addresses;
    modalRef.componentInstance.action = action;
    modalRef.componentInstance.specialFieldValue = specialFieldValue;
    modalRef.componentInstance.billToAddress = billTo;
  }

  saveClient() {
    Object.assign(this.client, this.clientForm.value);
    if (/^\s*$/.test(this.client.Name)) {
      this.onUpdateError('Client Name cannot be null or blank');
    } else {
      this.clientService.saveClient(this.client)
        .subscribe(c => {
          this.client['Organization ID'] = c['Organization ID'];
          if (!this.editing) {
            this.preferencesService.setPreference('CUSTOMER', this.client['Organization ID'], 'special-fields', { Value: 'Standard' })
              .subscribe(() => {
                this.getSpecialFieldsPreferences(c['Organization ID']);
                this.editing = true;
              });
          }
          this.onUpdateSuccess();
        }, error => {
          const err = error.error;
          this.onUpdateError(err.message);
        });
    }
  }

  private onClientSubscription(client: Organization) {
    this.clientForm = this.formBuilder.group({
      Name: [client.Name, Validators.required],
      Description: [client.Description, Validators.required],
      BroadcastMessage: ['']
    });

    if (client['Organization ID']) {
      this.clientService.getPreferences(client['Organization ID'])
        .subscribe(prefs => {
          this.theme = prefs['theme-color'].Value || 'tessco-purple';
          this.findLogo(prefs['organization-logo'].Value);
        });
    } else {
      this.theme = 'tessco-purple';
    }
  }

  private checkPermissions(clientId: string) {
    const context = `ORGANIZATION~${clientId}`;
    let roleRequest = new RoleRequest('MARKET_DELETE', context);
    roleRequest.setRoleAndContextMap('MARKET_UPDATE', context);
    this.authService.getUserRoles(roleRequest).subscribe(roles => {
      this.canDeleteMarket = roles.getRoleMap('MARKET_DELETE');
      this.canUpdateMarket = roles.getRoleMap('MARKET_UPDATE');
    });
  }

  private getMarkets(clientId: string) {
    this.marketService.getMarkets(clientId)
      .subscribe(markets => {
        this.markets = markets
        this.markets = this.markets.filter(market => this.userService.isMarketPermittedForCurrentUser(market.id));
      });
  }

  private getMarketsV2(orgId: string) {
    this.marketService.getMarkets(orgId)
      .subscribe(markets => {
        this.markets = markets
        this.markets = this.markets.filter(market => this.userService.isMarketPermittedForCurrentUser(market.id));
      });
  }

  private getReports(clientId: string) {
    this.userService.getReports(clientId)
      .subscribe(reports => reports !== null ? this.reports = reports : this.reports = []);
  }

  private getSpecialFieldsPreferences(clientId: string) {
    this.preferencesService.getPreferences('CUSTOMER', clientId).subscribe(prefs => {
      this.selectedSpecialField = prefs['special-fields'].Value ? prefs['special-fields'].Value : 'Standard';
      this.specialFields = prefs['special-fields']['Available Values'];
    });
  }

  setSpecialField(option: string) {
    this.selectedSpecialField = option;
    this.preferencesService.setPreference('CUSTOMER', this.client['Organization ID'], 'special-fields', { Value: option })
      .subscribe();
      this.onUpdateSuccess();
  }

  private onUpdateSuccess() {
    this.toastr.success('Client saved!!!', 'Succcess!', {
      timeOut: 1000,
      positionClass: 'toast-bottom-right'
    });
  }

  private onUpdateError(error: string) {
    this.toastr.error(error, 'Error!', {
      timeOut: 5000,
      positionClass: 'toast-bottom-right'
    });
  }

  ngOnInit() {
    this.loading = true;
    if (this.client['Organization ID']) {
      this.checkPermissions(this.client['Organization ID']);
      this.getMarketsV2(this.client['Organization ID']);
      this.getReports(this.client['Organization ID']);
      this.getSpecialFieldsPreferences(this.client['Organization ID']);
    } else {
      this.editing = false;
    }
    this.loading = false;
    this.onClientSubscription.call(this, this.client);
    this.setBroadCastMessageValue();
  }

  setBroadCastMessageValue() {
    this.preferencesService.getPreferences('CUSTOMER', this.client['Organization ID']).subscribe(prefs => {
      let broadcastMessage = prefs['broadcast-message'].Value;
      this.broadcastMessageValue = broadcastMessage;
      this.clientForm.get('BroadcastMessage').setValue(broadcastMessage);
    });
  }

  onBroadcastMessageChange(event: any) {
    const inputValue = event.target.value;
    console.log('Broadcast Message value changed to:', inputValue);
    this.preferencesService.setPreference('CUSTOMER', this.client['Organization ID'], 'broadcast-message', { Value: inputValue })
      .subscribe();
    this.onUpdateSuccess();
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3408204168/src/bitbucket.org/tesscotds/webclient/src/app/management/client-edit/client-edit.component.ts